import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CountryType } from '@core/entity/locale';
import { getTranslationProvider } from '@core/i18n/getTranslationProvider';
import { TranslatableFormDialogBaseComponent } from '@core/i18n/translatable-form-dialog-base.component';
import { TranslatableString } from '@core/i18n/TranslatableString';
import { requiredAndWithoutSpaces } from '@core/misc/required-without-spaces.validator';
import { validateFormSocialSecurityNumber } from '@core/misc/social-security-number.validator';
import { useValidatorsIf } from '@core/misc/use-validators-if.validator';
import { formatSocialSecurityNumberWithCentry } from '@core/misc/validation-helper';
import { LundifySubscriptionsService } from '@core/services/lundify-subscriptions.service';
import { ToastActions } from '@core/state/toast/toast.actions';
import { ClientType } from '@core/state/types';
import { LundifySubscriptionUser } from '@core/state/types/lundify-subscription-response.types';
import { BlFrontendAlertComponent } from '@core/wrappers/bl-frontend-alert.component';
import { BlFrontendButtonComponent } from '@core/wrappers/bl-frontend-button.component';
import { TranslocoModule } from '@jsverse/transloco';
import { DialogData, DialogResult } from '@shared/components/form-dialog-base/form-dialog-base.component';
import { UserSelectorComponent } from '@shared/components/user-selector/user-selector.component';
import { PrimeNgModule } from '@shared/primeng.module';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { LundifyUserFormType } from '../types';
import { UserSelectors } from 'src/app/core/state/users/users.selectors';
import { map, take } from 'rxjs';
import { BlFrontendTextInputComponent } from 'src/app/core/wrappers/bl-frontend-text-input.component';

interface Data extends DialogData {
  client: ClientType;
  country: CountryType;
  isAnnualSubscription: boolean;
}

interface Result extends DialogResult {
  user: LundifySubscriptionUser | null;
}

const TRANSLATION_SCOPE = 'upsertUserDialogComponent';

@Component({
  selector: 'app-upsert-user-dialog',
  templateUrl: './upsert-user-dialog.component.html',
  standalone: true,
  imports: [
    CommonModule,
    BlFrontendButtonComponent,
    ReactiveFormsModule,
    TranslocoModule,
    PrimeNgModule,
    BlFrontendAlertComponent,
    UserSelectorComponent,
    BlFrontendTextInputComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [getTranslationProvider(TRANSLATION_SCOPE)],
})
export class UpsertUserDialogComponent extends TranslatableFormDialogBaseComponent<Data, Result> {
  formGroup: FormGroup<LundifyUserFormType>;
  errorMessage: WritableSignal<string | null>;
  isAnnualSubscription: boolean;

  private lundifySubscriptionService;

  get isValid(): boolean {
    return this.formGroup.valid;
  }

  get isSwedishFirm(): boolean {
    return this.config.data.country === 'SE';
  }

  constructor(ref: DynamicDialogRef, config: DynamicDialogConfig<Data>) {
    super(ref, config);
    this.lundifySubscriptionService = inject(LundifySubscriptionsService);
    this.formGroup = this.createForm(this.config.data.country); // use country from firm
    this.errorMessage = signal(null);
    this.isAnnualSubscription = this.config.data.isAnnualSubscription;
  }

  protected static override getTranslationScope(): string {
    return TRANSLATION_SCOPE;
  }

  protected static override getDialogConfig(
    translation: Record<string, string>,
  ): Omit<DynamicDialogConfig<Data>, 'data'> {
    return {
      header: new TranslatableString(translation.title) as unknown as string,
      modal: true,
      dismissableMask: true,
      draggable: false,
      width: '500px',
    };
  }

  onSocialSecurityNumberBlur(): void {
    const socialSecurityNumberControl = this.formGroup.get('socialSecurityNumber');
    if (socialSecurityNumberControl) {
      const value = socialSecurityNumberControl.value as string;
      const formattedValue = formatSocialSecurityNumberWithCentry(value);
      if (formattedValue) {
        socialSecurityNumberControl.setValue(formattedValue);
      }
    }
  }

  onUserSelected(userId: number): void {
    // when user is selected from dropdown, we want to autofill email and name if available
    // this is only possible for annual subscription where we have access to all users in the system
    if (!this.isAnnualSubscription) {
      return;
    }

    this.store
      .select(UserSelectors.activeUsersWithoutAll)
      .pipe(
        map((users) => users.find((user) => user.id === userId)),
        take(1),
      )
      .subscribe((user) => {
        if (user) {
          this.formGroup.patchValue({
            email: user.email,
            name: user.name,
          });
        }
      });
  }

  saveAndCreateAdmin(): void {
    const client = this.config.data.client;

    this.errorMessage.set(null);

    this.lundifySubscriptionService
      .addSubscriptionUser(client.id, this.formGroup.value as LundifySubscriptionUser)
      .subscribe({
        next: (newUser: LundifySubscriptionUser) => {
          const summary = this.translate('userAddedToastTitle');
          const detail = this.translate('userAddedToastDetail');
          this.store.dispatch(ToastActions.showInfoMessage({ summary, detail }));
          this.ref.close({ user: newUser });
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error: (error: any) => {
          if (error?.error?.message === 'LUNDIFY_USER_EMAIL_PHONE_MISMATCH') {
            this.errorMessage.set(this.translate('userEmailPhoneMismatchMessage'));
            return;
          }
          this.errorMessage.set(this.translate('userAddFailedMessage'));
        },
      });
  }

  private createForm(country: CountryType): FormGroup<LundifyUserFormType> {
    const useValidatorIfCountry = (useForCountry: CountryType) => () => {
      return country === useForCountry;
    };

    return new FormGroup<LundifyUserFormType>({
      name: new FormControl<string>('', [requiredAndWithoutSpaces]),
      socialSecurityNumber: new FormControl<string>('', [
        useValidatorsIf(useValidatorIfCountry('SE'), [Validators.required, validateFormSocialSecurityNumber]),
      ]),
      email: new FormControl<string>('', [Validators.required, Validators.email]),
      phoneNumber: new FormControl<string>('', [
        useValidatorsIf(useValidatorIfCountry('NL'), requiredAndWithoutSpaces),
      ]),
      role: new FormControl<'admin' | 'user'>('admin'),
    });
  }
}
